import { ReactNode } from 'react'

import { Stack, Text } from '@mantine/core'

import IconAlertTriangle from 'src/icons/IconAlertTriangle'
import IconCheckmarkCircle from 'src/icons/IconCheckmarkCircle'
import IconInfoCircle from 'src/icons/IconInfoCircle'

export type OptOutStateTone = 'success' | 'warning' | 'info'

const TONES = {
  success: {
    Icon: IconCheckmarkCircle,
    ring: 'bg-doubleNickel-success-50 border-doubleNickel-success-200',
    stroke: 'stroke-doubleNickel-success-700',
  },
  warning: {
    Icon: IconAlertTriangle,
    ring: 'bg-doubleNickel-warning-50 border-doubleNickel-warning-200',
    stroke: 'stroke-doubleNickel-warning-700',
  },
  info: {
    Icon: IconInfoCircle,
    ring: 'bg-doubleNickel-brand-25 border-doubleNickel-brand-200',
    stroke: 'stroke-doubleNickel-brand-700',
  },
}

interface OptOutStateScreenProps {
  tone: OptOutStateTone
  title: string
  children: ReactNode
  details?: { label: string; value: string }[]
  note?: string
  footer?: string
}

const OptOutStateScreen = ({
  tone,
  title,
  children,
  details,
  note,
  footer,
}: OptOutStateScreenProps) => {
  const { Icon, ring, stroke } = TONES[tone]

  return (
    <Stack gap={0}>
      <Stack gap="md" align="center" className="text-center">
        <div
          className={`flex h-12 w-12 items-center justify-center rounded-full border ${ring}`}
        >
          <Icon className={`h-6 w-6 fill-none ${stroke}`} />
        </div>
        <Text className="text-lg font-semibold text-doubleNickel-gray-900">
          {title}
        </Text>
        <Text className="text-sm leading-relaxed text-doubleNickel-gray-600">
          {children}
        </Text>
      </Stack>

      {details && (
        <Stack
          gap="sm"
          className="mt-7 rounded-lg border border-doubleNickel-gray-200 bg-doubleNickel-gray-50 p-4"
        >
          {details.map((detail) => (
            <div key={detail.label} className="flex justify-between gap-4">
              <Text className="shrink-0 text-xs text-doubleNickel-gray-500">
                {detail.label}
              </Text>
              <Text className="text-right text-xs font-medium text-doubleNickel-gray-700">
                {detail.value}
              </Text>
            </div>
          ))}
        </Stack>
      )}

      {note && (
        <div className="mt-7 rounded-lg border border-doubleNickel-gray-200 bg-doubleNickel-gray-50 p-4">
          <Text className="text-xs leading-5 text-doubleNickel-gray-600">
            {note}
          </Text>
        </div>
      )}

      {footer && (
        <Text className="pt-6 text-center text-xs leading-5 text-doubleNickel-gray-500">
          {footer}
        </Text>
      )}
    </Stack>
  )
}

export default OptOutStateScreen
